import React, { useState, useEffect } from 'react';
import { Card, Table, Form, Row, Col, Select, DatePicker, Button, Space, Tag, message, Tooltip, Modal, Input, Checkbox, Upload, Dropdown } from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  EyeOutlined,
  DollarOutlined,
  BellOutlined,
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { CONTRACT_API, TENANT_API, ELECTRICITY_API, WATER_API, INVOICE_API } from '../constants/api';
import axios from '../services/axiosConfig';
import PaymentModal from '../components/rent/PaymentModal';
import InvoiceDetailModal from '../components/rent/InvoiceDetailModal';
import CreateInvoiceModal from '../components/rent/CreateInvoiceModal';
import EditInvoiceModal from '../components/rent/EditInvoiceModal';
import ActionButtons from '../components/common/ActionButtons';
import moment from 'moment';

const RentInvoices = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    status: undefined,
    contractId: undefined,
    factoryId: undefined,
    fromDate: undefined,
    toDate: undefined,
    invoiceType: undefined,
    tenantID: undefined
  });
  const [form] = Form.useForm();
  const [tenants, setTenants] = useState([]);
  const [contractList, setContractList] = useState([]);

  // Modal states
  const [paymentModal, setPaymentModal] = useState({ visible: false, record: null });
  const [detailModal, setDetailModal] = useState({ visible: false, record: null });
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState({ visible: false, record: null });
  const [reminderModal, setReminderModal] = useState(false);
  const [reminderOptions, setReminderOptions] = useState({
    rentInvoice: true,
    serviceInvoice: true,
    electricityBill: false,
    waterBill: false,
    wasteWaterBill: false
  });

  const [electricityBills, setElectricityBills] = useState([]);
  const [waterBills, setWaterBills] = useState([]);
  const [wasteWaterBills, setWasteWaterBills] = useState([]);
  const [electricityMonths, setElectricityMonths] = useState([]);
  const [waterMonths, setWaterMonths] = useState([]);
  const [wasteWaterMonths, setWasteWaterMonths] = useState([]);
  const [selectedElectricityMonths, setSelectedElectricityMonths] = useState([]);
  const [selectedWaterMonths, setSelectedWaterMonths] = useState([]);
  const [selectedWasteWaterMonths, setSelectedWasteWaterMonths] = useState([]);

  // State cho import excel
  const [importLoading, setImportLoading] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [grandTotals, setGrandTotals] = useState({
    totalAmount: 0,
    totalPaid: 0,
    totalRemaining: 0,
  });

  const calcTotalsFromInvoices = (invoices) => {
    const list = Array.isArray(invoices) ? invoices : [];
    return list.reduce(
      (acc, item) => {
        const amount = Number(item.Amount ?? item.amount) || 0;
        const paid = Number(item.PaidAmount ?? item.paidAmount) || 0;
        acc.totalAmount += amount;
        acc.totalPaid += paid;
        acc.totalRemaining += amount - paid;
        return acc;
      },
      { totalAmount: 0, totalPaid: 0, totalRemaining: 0 }
    );
  };

  const fetchGrandTotals = async (filtersToUse) => {
    try {
      const countRes = await axios.get(INVOICE_API.ALL, {
        params: { page: 1, limit: 1, ...filtersToUse },
      });
      const payload = countRes.data?.data || {};
      const summary = payload.summary;
      if (summary) {
        const totalAmount = Number(summary.totalAmount) || 0;
        const totalPaid = Number(summary.totalPaid) || 0;
        setGrandTotals({
          totalAmount,
          totalPaid,
          totalRemaining: totalAmount - totalPaid,
        });
        return;
      }
      const total = payload.pagination?.total || 0;
      if (total === 0) {
        setGrandTotals({ totalAmount: 0, totalPaid: 0, totalRemaining: 0 });
        return;
      }
      const allRes = await axios.get(INVOICE_API.ALL, {
        params: { page: 1, limit: total, ...filtersToUse },
      });
      const invoices = allRes.data?.data?.invoices || [];
      setGrandTotals(calcTotalsFromInvoices(invoices));
    } catch {
      setGrandTotals({ totalAmount: 0, totalPaid: 0, totalRemaining: 0 });
    }
  };

  const fetchInvoices = async (params = {}) => {
    try {
      setLoading(true);

      // Sử dụng filters được truyền từ params hoặc từ state
      const filtersToUse = params.filters || filters;

      const response = await axios.get(INVOICE_API.ALL, {
        params: {
          page: params.current || pagination.current,
          limit: params.pageSize || pagination.pageSize,
          ...filtersToUse, // Sử dụng filters đã xác định
        },
      });

      // Map lại dữ liệu từ API để phù hợp với cấu trúc Table
      const mappedData = response.data.data.invoices.map(item => ({
        id: item.id, // Dùng id từ API tổng hợp
        invoiceNumber: item.invoiceNumber,
        factoryName: item.factoryName,
        tenantName: item.tenantName,
        tenantID: item.tenantID, // Sử dụng tenantID từ API tổng hợp nếu có
        contractID: item.contractId,
        period: item.period,
        amount: item.amount,
        paidAmount: item.paidAmount,
        remainingAmount: item.amount - item.paidAmount,
        dueDate: item.dueDate ? new Date(item.dueDate).toLocaleDateString('vi-VN') : '',
        status: item.status === 'unpaid' ? 'pending' : item.status,
        invoiceType: item.invoiceType || 'rent',
        isDebt: item.isDebt
      }));
      setData(mappedData);
      setPagination({
        ...pagination,
        total: response.data.data.pagination.total,
        current: response.data.data.pagination.page,
        pageSize: response.data.data.pagination.limit,
      });

      if (!params.skipTotalsFetch) {
        fetchGrandTotals(filtersToUse);
      }
    } catch (error) {
      message.error('Không thể tải danh sách hóa đơn');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination, filters, sorter) => {
    fetchInvoices({
      ...newPagination,
      skipTotalsFetch: true,
      sortField: sorter.field,
      sortOrder: sorter.order,
      ...filters,
    });
  };

  
  const handleExport = async () => {
    try {
      setLoading(true);
      const values = form.getFieldsValue() || {};
      let url = `${INVOICE_API.EXPORT}?`;
      if (values.startDate) {
        const start = typeof values.startDate.format === 'function' ? values.startDate.format('YYYY-MM-DD') : values.startDate;
        url += `startDate=${start}&`;
      }
      if (values.endDate) {
        const end = typeof values.endDate.format === 'function' ? values.endDate.format('YYYY-MM-DD') : values.endDate;
        url += `endDate=${end}&`;
      }
      if (values.status && values.status !== 'all') {
        url += `status=${values.status}&`;
      }
      
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'HoaDonTongHop.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success('Xuất file thành công');
    } catch (error) {
      console.error('Lỗi khi xuất file:', error);
      message.error('Có lỗi xảy ra khi xuất file');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async (options) => {
    const { file, onSuccess, onError } = options;
    const formData = new FormData();
    formData.append('file', file);
    try {
      setImportLoading(true);
      const res = await axios.post(INVOICE_API.IMPORT, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      message.success(res.data.message || 'Import thành công');
      if (res.data.errors && res.data.errors.length > 0) {
        Modal.warning({
          title: 'Chi tiết lỗi Import',
          content: (
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {res.data.errors.map((err, i) => <div key={i}>{err}</div>)}
            </div>
          )
        });
      }
      setImportModal(false);
      fetchInvoices();
      if (onSuccess) onSuccess('ok');
    } catch (err) {
      console.error('Import error:', err);
      message.error(err.response?.data?.message || 'Có lỗi xảy ra khi import');
      if (onError) onError(err);
    } finally {
      setImportLoading(false);
    }
  };

  
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(INVOICE_API.TEMPLATE, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'Template_HoaDonTongHop.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Lỗi khi tải file mẫu:', error);
      message.error('Có lỗi xảy ra khi tải file mẫu');
    }
  };

  const handleSearch = (values) => {
    // Xử lý định dạng ngày tháng trước khi gửi API
    const formattedValues = { ...values };

    if (formattedValues.startDate) {
      // Chuyển đổi sang định dạng ngày/tháng/năm
      formattedValues.startDate = formattedValues.startDate.format('DD/MM/YYYY');
    }

    if (formattedValues.endDate) {
      formattedValues.endDate = formattedValues.endDate.format('DD/MM/YYYY');
    }

    // Lưu giá trị mới vào state
    setFilters(formattedValues);
    setPagination({ ...pagination, current: 1 });

    // Gọi API với giá trị đã định dạng
    fetchInvoices({
      current: 1,
      pageSize: pagination.pageSize,
      filters: formattedValues  // Truyền filters mới vào hàm fetchInvoices
    });
  };

  const handleCreateInvoice = async (values) => {
    try {
      await axios.post(INVOICE_API.CREATE, values);
      message.success('Tạo hóa đơn thành công');
      setCreateModal(false);
      fetchInvoices();
    } catch (error) {
      message.error('Lỗi khi tạo hóa đơn');
    }
  };

  const sendReminder = async (record) => {
    try {
      let invoiceIds = [];

      // Nếu gửi cho 1 hóa đơn cụ thể
      if (record && record.id) {
        invoiceIds.push(record.id);
      }
      // Nếu gửi cho nhiều hóa đơn được chọn
      else if (selectedRows && selectedRows.length > 0) {
        invoiceIds = selectedRows.map(row => row.id);
      } else {
        message.warning('Vui lòng chọn ít nhất một hóa đơn');
        return;
      }

      await axios.post(INVOICE_API.SEND_REMINDER, {
        invoiceIds: invoiceIds
      });

      message.success(`Đã gửi nhắc nhở thanh toán cho ${invoiceIds.length} hóa đơn`);

      // Reset selected items sau khi gửi nhắc nhở hàng loạt
      if (!record || !record.id) {
        setSelectedRowKeys([]);
        setSelectedRows([]);
      }
    } catch (error) {
      console.error('Lỗi khi gửi nhắc nhở:', error);
      message.error('Không thể gửi nhắc nhở');
    }
  };

  const checkOverdueInvoices = async () => {
    try {
      const { data } = await axios.get(INVOICE_API.OVERDUE);
      if (data.length > 0) {
        message.warning(`Có ${data.length} hóa đơn quá hạn cần xử lý`);
      }
    } catch (error) {
      console.error('Không thể kiểm tra hóa đơn quá hạn:', error);
    }
  };

  const handleDeleteInvoice = (record) => {
    Modal.confirm({
      title: 'Xác nhận xóa hóa đơn tổng hợp',
      content: `Bạn có chắc chắn muốn xóa hóa đơn ${record.invoiceNumber} không?`,
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await axios.delete(INVOICE_API.DELETE(record.invoiceNumber));
          message.success('Xóa hóa đơn thành công');
          fetchInvoices();
        } catch (error) {
          console.error('Lỗi khi xóa hóa đơn:', error);
          message.error('Không thể xóa hóa đơn');
        }
      },
    });
  };

  const columns = [
    {
      title: 'Mã hóa đơn',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
    },
    {
      title: 'Xưởng',
      dataIndex: 'factoryName',
      key: 'factoryName',
    },
    {
      title: 'Khách thuê',
      dataIndex: 'tenantName',
      key: 'tenantName',
    },
    {
      title: 'Loại hóa đơn',
      dataIndex: 'invoiceType',
      key: 'invoiceType',
      render: (type) => {
        const typeMap = {
          'monthly': 'Hóa đơn tổng hợp',
          'rent': 'Tiền thuê xưởng',
          'service': 'Dịch vụ & Hạ tầng',
          'electricity': 'Tiền điện',
          'water': 'Nước sạch',
          'wastewater': 'Nước thải'
        };
        return typeMap[type] || 'Khác';
      }
    },
    {
      title: 'Kỳ thanh toán',
      dataIndex: 'period',
      key: 'period',
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => new Intl.NumberFormat('vi-VN').format(amount),
    },
    {
      title: 'Đã thanh toán',
      key: 'paidAmount',
      render: (_, record) => {
        const paidAmount = record.paidAmount || 0;
        return new Intl.NumberFormat('vi-VN').format(paidAmount);
      }
    },
    {
      title: 'Còn thiếu',
      key: 'remainingAmount',
      render: (_, record) => {
        const paidAmount = record.paidAmount || 0;
        const remainingAmount = record.amount - paidAmount;
        return new Intl.NumberFormat('vi-VN').format(remainingAmount);
      }
    },
    {
      title: 'Hạn thanh toán',
      dataIndex: 'dueDate',
      key: 'dueDate',
    },
    {
      title: 'Công nợ',
      dataIndex: 'isDebt',
      key: 'isDebt',
      render: (isDebt) => isDebt ? <Tag color="red">Có</Tag> : <Tag color="green">Không</Tag>
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusConfig = {
          pending: { color: 'gold', text: 'Chờ thanh toán' },
          unpaid: { color: 'gold', text: 'Chưa thanh toán' },
          paid: { color: 'green', text: 'Đã thanh toán' },
          overdue: { color: 'red', text: 'Quá hạn' },
          partial: { color: 'orange', text: 'Chưa thanh toán đủ' },
        };
        return statusConfig[status] ? <Tag color={statusConfig[status].color}>{statusConfig[status].text}</Tag> : <Tag>{status}</Tag>;
      },
    },
    {
      title: 'Thao tác',
      key: 'action',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <ActionButtons
          module="rent_invoices"
          onView={() => setDetailModal({ visible: true, record })}
          onAdd={() => setCreateModal(true)}
          onEdit={() => setEditModal({ visible: true, record })}
          onPay={() => setPaymentModal({ visible: true, record })}
          onRemind={() => {
            Modal.confirm({
              title: 'Xác nhận gửi nhắc nhở',
              content: `Gửi nhắc nhở thanh toán cho hóa đơn ${record.invoiceNumber}?`,
              okText: 'Gửi',
              cancelText: 'Hủy',
              onOk: () => sendReminder(record)
            });
          }}
          onDelete={() => handleDeleteInvoice(record)}
          record={record}
          showAdd={false}
          showPay={true}
          showRemind={true}
          showImport={false}
          showExport={false}
          showPrint={false}
          showShare={false}
          showApprove={false}
          showReject={false}
          showSync={false}
          disabled={loading}
          loading={loading}
        />
      ),
    },
  ];

  const fetchTenants = async () => {
    try {
      const response = await axios.get(TENANT_API.ALL);
      if (response.data && response.data.tenants) {
        setTenants(response.data.tenants.map(tenant => ({
          value: tenant.TenantID,
          label: tenant.TenantName
        })));
      }
    } catch (error) {
      console.error('Lỗi khi tải danh sách khách hàng:', error);
    }
  };

  const fetchContracts = async () => {
    try {
      const response = await axios.get(CONTRACT_API.LIST);
      setContractList(response.data.contracts || []);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchTenants();
    fetchContracts();
    checkOverdueInvoices();
    const interval = setInterval(checkOverdueInvoices, 24 * 60 * 60 * 1000); // Kiểm tra mỗi 24h
    return () => clearInterval(interval);
  }, []);

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys);
      setSelectedRows(selectedRows);
    },
  };



  return (
    <>
      <Card
        title="Quản lý Hóa đơn tiền thuê"
        style={{ margin: '16px' }}
        extra={
          <Space>
            
            <Button 
              onClick={handleExport}
              loading={loading}
              icon={<DownloadOutlined />}
            >
              Xuất Excel
            </Button>
            <Button 
              type="dashed"
              onClick={() => setImportModal(true)}
              icon={<UploadOutlined />}
            >
              Nhập Excel
            </Button>
            <Button type="primary" onClick={() => setCreateModal(true)}>
              <PlusOutlined /> Thêm mới
            </Button>
            <Button 
              type="default" 
              danger
              disabled={selectedRowKeys.length === 0}
              onClick={() => {
                Modal.confirm({
                  title: 'Xác nhận gửi nhắc nhở',
                  content: `Gửi nhắc nhở thanh toán cho ${selectedRowKeys.length} hóa đơn đã chọn?`,
                  okText: 'Gửi',
                  cancelText: 'Hủy',
                  onOk: () => sendReminder(selectedRowKeys)
                });
              }}
            >
              <BellOutlined /> Gửi nhắc nhở hàng loạt
            </Button>
          </Space>
        }
      >
        <Form form={form} onFinish={handleSearch} style={{ marginBottom: '16px' }}>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="status" label="Trạng thái">
                <Select
                  allowClear
                  placeholder="Chọn trạng thái"
                  options={[
                    { value: 'pending', label: 'Chờ thanh toán' },
                    { value: 'unpaid', label: 'Chưa thanh toán' },
                    { value: 'paid', label: 'Đã thanh toán' },
                    { value: 'overdue', label: 'Quá hạn' },
                    { value: 'partial', label: 'Chưa thanh toán đủ' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="invoiceType" label="Loại hóa đơn">
                <Select
                  allowClear
                  placeholder="Chọn loại hóa đơn"
                  options={[
                    { value: 'all', label: 'Tất cả' },
                    { value: 'monthly', label: 'Hóa đơn tổng hợp' },
                    { value: 'rent', label: 'Tiền thuê xưởng' },
                    { value: 'service', label: 'Dịch vụ & Hạ tầng' },
                    { value: 'electricity', label: 'Tiền điện' },
                    { value: 'water', label: 'Nước sạch' },
                    { value: 'wastewater', label: 'Nước thải' }
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="startDate" label="Từ ngày">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="endDate" label="Đến ngày">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="tenantID" label="Khách hàng">
                <Select
                  placeholder="Chọn khách hàng"
                  showSearch
                  allowClear
                  options={tenants}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24} style={{ textAlign: 'right' }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} htmlType="submit">
                  Tìm kiếm
                </Button>
                <Button icon={<ReloadOutlined />} onClick={() => {
                  form.resetFields();
                  const emptyFilters = {};
                  setFilters(emptyFilters);
                  fetchInvoices({
                    current: 1,
                    pageSize: pagination.pageSize,
                    filters: emptyFilters
                  });
                }}>
                  Làm mới
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>

        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={data}
          summary={() => {
            const { totalAmount, totalPaid, totalRemaining } = grandTotals;
            const fmt = (n) => new Intl.NumberFormat('vi-VN').format(n || 0);

            // Lưu ý: có rowSelection => thêm 1 cột checkbox ở đầu
            // columns: [checkbox] | Mã hóa đơn | Xưởng | Khách thuê | Loại | Kỳ | Số tiền | Đã thanh toán | Còn thiếu | Hạn | Trạng thái | Thao tác
            return (
              <Table.Summary fixed="bottom">
                <Table.Summary.Row style={{ fontWeight: 700, backgroundColor: '#f0f2f5' }}>
                  <Table.Summary.Cell index={0} colSpan={6}>
                    Tổng (tất cả {pagination.total} hóa đơn theo bộ lọc)
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="left" style={{ textAlign: 'left' }}>{fmt(totalAmount)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={7} align="left" style={{ textAlign: 'left' }}>{fmt(totalPaid)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={8} align="left" style={{ textAlign: 'left' }}>{fmt(totalRemaining)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={9} colSpan={3} />
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Tổng số ${total} hóa đơn`,
            pageSizeOptions: ['10', '20', '50', '100']
          }}
          loading={loading}
          onChange={handleTableChange}
          rowKey="id"
          scroll={{ x: 1500 }}
          bordered
          size="middle"
          style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
          }}
        />
      </Card>

      <Modal
        title="Nhập Hóa Đơn Tổng Hợp"
        open={importModal}
        onCancel={() => setImportModal(false)}
        footer={null}
      >
        <p>Vui lòng chọn file Excel đúng định dạng. Bạn có thể <a onClick={handleDownloadTemplate}>Tải file mẫu tại đây</a>, sau đó điền dữ liệu vào file tải về và <b>Nhập Excel</b> trở lại.</p>
        <p><i>Lưu ý: Nếu trùng Mã hóa đơn, hệ thống sẽ ghi đè dữ liệu.</i></p>
        <Upload
          accept=".xlsx, .xls"
          customRequest={handleImport}
          showUploadList={false}
        >
          <Button type="primary" loading={importLoading} icon={<UploadOutlined />}>
            Chọn file và Import
          </Button>
        </Upload>
      </Modal>

      <PaymentModal
        visible={paymentModal.visible}
        record={paymentModal.record}
        onCancel={() => setPaymentModal({ visible: false, record: null })}
        onSuccess={() => {
          setPaymentModal({ visible: false, record: null });
          fetchInvoices();
        }}
      />

      <InvoiceDetailModal
        visible={detailModal.visible}
        record={detailModal.record}
        onCancel={() => setDetailModal({ visible: false, record: null })}
      />

      <CreateInvoiceModal
        visible={createModal}
        onCancel={() => setCreateModal(false)}
        onSuccess={() => {
          setCreateModal(false);
          fetchInvoices();
        }}
      />

      <EditInvoiceModal
        visible={editModal.visible}
        record={editModal.record}
        onCancel={() => setEditModal({ visible: false, record: null })}
        onSuccess={() => {
          setEditModal({ visible: false, record: null });
          fetchInvoices();
        }}
      />
    </>
  );
};

export default RentInvoices;